import { ArrowLeft } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaUpload, FaSpinner, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import API from "../../config/config";
import DrivingLicensePreview from "../../components/Driver/DrivingLicensePreview"; // adjust path as needed
import Loader from "../../components/Loader";

const VerifyLicenseWithUpload = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    dlNumber: "",
    fullName: "",
    dob: "",
    doi: "",
    doe: "",
    dlfront: "",
    dlback: "",
  });
  const [errors, setErrors] = useState({});
  const [dlFrontFile, setDlFrontFile] = useState(null);
  const [dlBackFile, setDlBackFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("userPublicToken");
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingFront, setIsEditingFront] = useState(false);
  const [isEditingBack, setIsEditingBack] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [showFullImageFront, setShowFullImageFront] = useState(false);
  const [showFullImageBack, setShowFullImageBack] = useState(false);

  const navigate = useNavigate();

  const today = new Date();
  const minDOB = new Date(
    today.getFullYear() - 21,
    today.getMonth(),
    today.getDate()
  );
  const maxDOI = new Date(
    today.getFullYear() - 1,
    today.getMonth(),
    today.getDate()
  );
  const minDOE = new Date(
    today.getFullYear() + 1,
    today.getMonth(),
    today.getDate()
  );

  // Get existing DL details if passed from dashboard
  const existing = location.state?.dlDetails;

  useEffect(() => {
    const fetchDL = async () => {
      try {
        const res = await fetch(API.ADMIN.USERDASHBOARD, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch profile");
        const data = await res.json();
        const dl = data.data?.drivingLincenseDetails;
        console.log("DL details from API:", dl);
        console.log("API DL details:", dl);
        if (dl) {
          setFormData({
            dlNumber: dl.dlNumber || "",
            fullName: dl.nameToMatch || "",
            dob: toInputDateFormat(dl.dateOfBirth) || "",
            doi: toInputDateFormat(dl.dateOfIssue) || "",
            doe: toInputDateFormat(dl.dateOfExpire) || "",
            dlfront: dl.dlfront || "",
            dlback: dl.dlback || "",
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDL();
  }, [token]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    let filteredValue = value;

    if (id === "fullName") {
      // Allow only letters and spaces
      filteredValue = value.replace(/[^A-Za-z\s]/g, "");
    } else if (id === "dlNumber") {
      // Allow only uppercase letters and digits
      filteredValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    }

    setFormData((prev) => ({ ...prev, [id]: filteredValue }));
  };

  const getFullYearsDifference = (fromDate, toDate) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);

    let years = to.getFullYear() - from.getFullYear();

    // If the month/day of `to` is before `from`, subtract a year
    if (
      to.getMonth() < from.getMonth() ||
      (to.getMonth() === from.getMonth() && to.getDate() < from.getDate())
    ) {
      years--;
    }

    return years;
  };

  const validate = () => {
    const newErrors = {};
    const { dob, doi, doe, fullName, dlNumber } = formData;

    if (!dlNumber) newErrors.dlNumber = "DL Number is required.";
    if (!fullName) newErrors.fullName = "Full Name is required.";

    if (!dob) {
      newErrors.dob = "Date of Birth is required.";
    } else if (getFullYearsDifference(dob, today) < 21) {
      newErrors.dob = "Age should be at least 21 years.";
    }

    if (!doi) {
      newErrors.doi = "Date of Issue is required.";
    } else if (getFullYearsDifference(doi, today) < 1) {
      newErrors.doi = "License must be issued at least 1 year ago.";
    }

    if (!doe) {
      newErrors.doe = "Date of Expiry is required.";
    } else if (getFullYearsDifference(today, doe) < 1) {
      newErrors.doe = "License must be valid for at least 1 more year.";
    }

    if (doi && doe) {
      const issueDate = new Date(doi);
      const expireDate = new Date(doe);
      if (!Number.isNaN(issueDate.getTime()) && !Number.isNaN(expireDate.getTime()) && expireDate <= issueDate) {
        newErrors.doe = "Date of Expiry must be after Date of Issue.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;
  };

  const handleLicenseSubmit = (e) => {
    e.preventDefault();
    console.log("Step 1 form submitted, validating...");
    if (validate()) {
      console.log("Validation passed, moving to step 2");
      setStep(2);
    } else {
      console.log("Validation failed");
    }
  };

  const uploadDocument = async (file, type) => {
    const headers = new Headers();
    headers.append("Authorization", "Bearer " + token);

    const formdata = new FormData();
    formdata.append("type", type);
    formdata.append("documentFor", "Driver");
    formdata.append("file", file, file.name);

    const response = await fetch(API.ADMIN.DOCUMENT_UPLOAD, {
      method: "POST",
      headers,
      body: formdata,
    });

    if (!response.ok) throw new Error(`Upload failed: ${type}`);
    const data = await response.json();
    return data.document?.documentUrl || null;
  };

  const handleUploadStepSubmit = (e) => {
    e.preventDefault();
    // Check if we have either existing images or new uploaded files
    const hasFrontImage = formData.dlfront || dlFrontFile;
    const hasBackImage = formData.dlback || dlBackFile;

    console.log("Step 2 validation:", {
      hasFrontImage,
      hasBackImage,
      formDataDlfront: formData.dlfront,
      formDataDlback: formData.dlback,
      dlFrontFile,
      dlBackFile,
    });

    if (!hasFrontImage || !hasBackImage) {
      alert(
        "Please ensure both front and back images of your license are available."
      );
      return;
    }
    console.log("Moving to step 3");
    setStep(3);
  };

  const handleFinalSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true); // Show loader immediately on submit
    setFlashMessage(null);

    try {
      let frontUrl = formData.dlfront;
      let backUrl = formData.dlback;

      if (dlFrontFile) frontUrl = await uploadDocument(dlFrontFile, "dlfront");
      if (dlBackFile) backUrl = await uploadDocument(dlBackFile, "dlback");

      const payload = {
        dlNumber: formData.dlNumber,
        nameToMatch: formData.fullName,
        dateOfBirth: formatDate(formData.dob),
        dateOfIssue: formatDate(formData.doi),
        dateOfExpire: formatDate(formData.doe),
        consent: "Y",
        consentText:
          "I hear by declare my consent agreement for fetching my information via ZOOP API",
        taskId: "f26eb21e-4c35-4491-b2d5-41fa0e545a34",
        state: "",
        dlfront: frontUrl,
        dlback: backUrl,
      };

      const response = await fetch(API.ADMIN.VERIFY_DRIVING_LICENSE, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let result = null;

      try {
        result = await response.json();
      } catch (parseErr) {
        throw new Error("Failed to parse server response.");
      }

      if (!response.ok || result?.success === false) {
        // Specific case: DL already associated
        if (
          result?.message ===
          "This driving license number is already associated with another driver."
        ) {
          setFlashMessage(result.message);
          setTimeout(() => {
            setFlashMessage(null);
            navigate("/user-dashboard");
          }, 500);
          return;
        }

        // General failure
        throw new Error(result?.message || "DL verification failed.");
      }

      // Check for specific in-progress message
      if (
        result.success === true &&
        result.message &&
        (result.message ===
          "Verification failed, but status saved as InProgress" ||
          result.message.includes("InProgress") ||
          result.message.includes("in progress"))
      ) {
        // ✅ Specific message matched — treat as "in progress"
        toast.info("Driving license verification is in progress.");
        setTimeout(() => navigate("/user-dashboard"), 1500);
      } else if (result.success) {
        // ✅ Normal success case
        toast.success("Driving license submitted successfully!");
        localStorage.setItem("dlName", formData.fullName);
        setTimeout(() => navigate("/user-dashboard"), 1500);
      } else {
        // ❌ Fallback success handling
        toast.success("Driving license verification is in progress.");
        localStorage.setItem("dlName", formData.fullName);
        setTimeout(() => navigate("/user-dashboard"), 1500);
      }
    } catch (err) {
      console.error("DL Submit Error:", err);
      setFlashMessage(err.message || "Verification failed.");
      setTimeout(() => setFlashMessage(null), 10000);
    } finally {
      setLoading(false);
    }
  };

  const dateFields = [
    { key: "dob", label: "Date of Birth" },
    { key: "doi", label: "Date of Issue" },
    { key: "doe", label: "Date of Expiry" },
  ];

  const nameFields = [
    { key: "dlNumber", label: "Driving License Number" },
    { key: "fullName", label: "Full Name" },
  ];

  function toInputDateFormat(dateStr) {
    // If already in yyyy-MM-dd, return as is
    if (!dateStr || /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Convert dd-MM-yyyy to yyyy-MM-dd
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  // Only show error globally, not loader
  if (error) return <div style={{ color: "red" }}>{error}</div>;

  return (
    <div className="max-w-lg mx-auto my-10 p-8 bg-white rounded-2xl shadow-2xl">
      {flashMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-100 text-red-700 px-6 py-3 rounded shadow z-50 text-center w-fit max-w-lg">
          {flashMessage}
          <div className="text-sm text-gray-500 mt-1">
            Redirecting if applicable...
          </div>
        </div>
      )}
      {/* Step 1: License Details Form */}
      {step === 1 && (
        <>
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate("/user-dashboard")}
              className="mr-3 text-gray-700 hover:text-indigo-600"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              Enter Driving License Details
            </h2>
          </div>
          <form onSubmit={handleLicenseSubmit} className="space-y-4 mb-8">
            {nameFields.map(({ key, label }) => (
              <div key={key}>
                <label className="block font-medium capitalize">{label}</label>
                <input
                  type="text"
                  id={key}
                  value={formData[key]}
                  onChange={handleChange}
                  placeholder={label}
                  className={`w-full border px-3 py-2 rounded ${
                    errors[key] ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors[key] && (
                  <p className="text-red-500 text-sm">{errors[key]}</p>
                )}
              </div>
            ))}
            {dateFields.map(({ key, label }) => (
              <div key={key}>
                <label htmlFor={key} className="block font-medium">
                  {label}
                </label>
                <input
                  type="date"
                  id={key}
                  name={key}
                  value={formData[key]}
                  onChange={handleChange}
                  max={
                    key === "dob"
                      ? minDOB.toISOString().split("T")[0]
                      : key === "doi"
                      ? maxDOI.toISOString().split("T")[0]
                      : undefined
                  }
                  min={
                    key === "doe"
                      ? minDOE.toISOString().split("T")[0]
                      : undefined
                  }
                  className={`w-full border px-3 py-2 rounded ${
                    errors[key] ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {/* Warnings and errors */}
                {key === "dob" && (
                  <p className="text-yellow-600 text-sm mt-1">
                    * Age should be minimum 21 years
                  </p>
                )}
                {key === "doi" && (
                  <p className="text-yellow-600 text-sm mt-1">
                    * Driving license issue date must be at least 1 year old
                  </p>
                )}
                {key === "doe" && (
                  <p className="text-yellow-600 text-sm mt-1">
                    * Driving license expiry date should be minimum 1 year from
                    today
                  </p>
                )}
                {errors[key] && (
                  <p className="text-red-500 text-sm">{errors[key]}</p>
                )}
              </div>
            ))}
            <button className="w-full bg-indigo-600 text-white py-2 rounded">
              Next
            </button>
          </form>
        </>
      )}

      {/* Step 2: Image Cards */}
      {step === 2 && (
        <>
          <div className="flex items-center mb-6">
            <button
              onClick={() => setStep(1)}
              className="mr-3 text-gray-700 hover:text-indigo-600"
              type="button"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              Upload Driving License Images
            </h2>
          </div>

          {/* DL Front Section */}
          <div className="mb-8 p-4 bg-gray-50 rounded-xl shadow flex flex-col items-center">
            <label className="block font-semibold mb-2 text-lg text-gray-700">
              DL Front Image:
            </label>

            {formData.dlfront || dlFrontFile ? (
              <>
                <img
                  src={
                    dlFrontFile
                      ? URL.createObjectURL(dlFrontFile)
                      : formData.dlfront
                  }
                  alt="DL Front"
                  className="w-full max-w-xs rounded-xl shadow-lg mb-3 border-2 border-indigo-200"
                />
                <div className="flex gap-4">
                  {formData.dlfront && !dlFrontFile && (
                    <button
                      onClick={() => setShowFullImageFront(true)}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <FaEye />
                      View Full Image
                    </button>
                  )}
                  <label className="cursor-pointer flex gap-2 items-center text-sm text-blue-600 hover:text-blue-800">
                    <FaUpload />
                    Change
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setDlFrontFile(e.target.files[0])}
                    />
                  </label>
                </div>
              </>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setDlFrontFile(e.target.files[0])}
                className="mb-2"
              />
            )}
          </div>

          {/* DL Back Section */}
          <div className="mb-8 p-4 bg-gray-50 rounded-xl shadow flex flex-col items-center">
            <label className="block font-semibold mb-2 text-lg text-gray-700">
              DL Back Image:
            </label>

            {formData.dlback || dlBackFile ? (
              <>
                <img
                  src={
                    dlBackFile
                      ? URL.createObjectURL(dlBackFile)
                      : formData.dlback
                  }
                  alt="DL Back"
                  className="w-full max-w-xs rounded-xl shadow-lg mb-3 border-2 border-indigo-200"
                />
                <div className="flex gap-4">
                  {formData.dlback && !dlBackFile && (
                    <button
                      onClick={() => setShowFullImageBack(true)}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <FaEye />
                      View Full Image
                    </button>
                  )}
                  <label className="cursor-pointer flex gap-2 items-center text-sm text-blue-600 hover:text-blue-800">
                    <FaUpload />
                    Change
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setDlBackFile(e.target.files[0])}
                    />
                  </label>
                </div>
              </>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setDlBackFile(e.target.files[0])}
                className="mb-2"
              />
            )}
          </div>

          <button
            className="w-full bg-indigo-600 text-white py-2 rounded"
            onClick={handleUploadStepSubmit}
          >
            Next: Preview
          </button>
        </>
      )}

      {/* Step 3: Preview & Confirm */}
      {step === 3 && (
        <>
          <div className="flex items-center mb-4">
            <button
              onClick={() => setStep(2)}
              type="button"
              className="mr-3 text-gray-700 hover:text-indigo-600"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">Preview</h2>
          </div>

          <DrivingLicensePreview
            data={formData}
            frontFile={dlFrontFile}
            backFile={dlBackFile}
          />

          <button
            className={`w-full mt-4 bg-green-600 text-white py-2 rounded flex items-center justify-center ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
            onClick={handleFinalSubmit}
            disabled={loading}
            type="button"
          >
            {loading && <FaSpinner className="animate-spin mr-2" />}
            {loading ? "Submitting..." : "Confirm & Submit"}
          </button>
        </>
      )}
      {showFullImageFront && formData.dlfront && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-full p-4">
            <img
              src={formData.dlfront}
              alt="DL Front Full View"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImageFront(false)}
              className="absolute top-2 right-2 bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {showFullImageBack && formData.dlback && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-4xl max-h-full p-4">
            <img
              src={formData.dlback}
              alt="DL Back Full View"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImageBack(false)}
              className="absolute top-2 right-2 bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifyLicenseWithUpload;
